/**
 * MemberHrPanel — HR-only "HR Records" card on the member detail page.
 *
 * SAFETY: rendered only when user.role === 'hr' (by the parent) AND every query
 * here is role-gated at the source, so a non-HR session never fetches this data.
 * The general member view uses its own object/queries — HR fields never mix in.
 *
 * Full CRUD: per-section edit modals, add/edit/delete children & evaluations,
 * file upload/download/delete.
 *
 * LAYOUT: below `lg` the sections render as one top-to-bottom stack (unchanged —
 * a natural thumb-scroll on mobile). At `lg`+ they split into four tabs to cut the
 * cognitive load of the full wall of fields: Personal (Children + Education),
 * Employment (with Vacation as its second column), Files, Evaluations.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Lock, Download, FileText, FileImage, FileSpreadsheet, FileType, User, GraduationCap,
  Briefcase, Users, ClipboardList, ChevronDown, ChevronRight,
  Pencil, Plus, Trash2, Upload, TreePalm,
} from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { DeleteConfirmModal } from '../ui/DeleteConfirmModal';
import { HrSectionModal } from './hr/HrSectionModal';
import { HrChildModal } from './hr/HrChildModal';
import { HrEvaluationModal } from './hr/HrEvaluationModal';
import { hrService } from '../../services/hr.service';
import { useToast } from '../../hooks/useToast';
import { handleApiError } from '../../lib/axios';
import { formatDate } from '../../lib/utils';
import type { HrSection, MemberChild, MemberEvaluation, MemberFile } from '../../types';

const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.pdf,.docx,.xlsx';
// Ghost-button look (matches the "Clear Filters" tertiary button): heading-colored
// label + blue icon, with a surface-hover fill (padding + rounding so the fill reads
// as a rounded chip). dangerLinkBtn keeps the destructive red (label + icon).
const linkBtn = 'inline-flex items-center gap-1 text-xs text-heading hover:bg-surface-hover rounded-md px-2 py-1 transition-colors [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400';
const dangerLinkBtn = 'inline-flex items-center gap-1 text-xs text-heading hover:bg-surface-hover rounded-md px-2 py-1 transition-colors [&>svg]:text-red-600 dark:[&>svg]:text-red-400';

type HrTab = 'personal' | 'employment' | 'files' | 'evaluations';

/**
 * Track whether we're at the `lg` breakpoint (1024px, matching Tailwind's `lg`
 * and the app's mobile/desktop divide). Initialised synchronously from matchMedia
 * so there's no first-paint flash between the two layouts.
 */
function useIsDesktop(): boolean {
  const query = '(min-width: 1024px)';
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

// Pick a file icon + accent colour from the extension. PDF/Word share the text-file
// glyph (lucide has no dedicated pdf/word icon), so colour distinguishes them.
function fileIconFor(name: string): { Icon: typeof FileText; color: string } {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return { Icon: FileImage, color: 'text-purple-500 dark:text-purple-400' };
  if (ext === 'pdf') return { Icon: FileText, color: 'text-red-500 dark:text-red-400' };
  if (['doc', 'docx'].includes(ext)) return { Icon: FileType, color: 'text-blue-500 dark:text-blue-400' };
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { Icon: FileSpreadsheet, color: 'text-green-600 dark:text-green-400' };
  return { Icon: FileText, color: 'text-meta' };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Field({ label, value }: { label: string; value: string | number | null }) {
  const empty = value === null || value === '' || value === undefined;
  return (
    <div>
      <dt className="text-sm font-medium text-meta">{label}</dt>
      <dd className="mt-1 text-sm text-heading whitespace-pre-wrap">
        {empty ? <span className="text-meta italic">Not set</span> : value}
      </dd>
    </div>
  );
}


function SectionHeader({ icon, title, action }: { icon: ReactNode; title: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-[15px]">
      <h3 className="text-base font-medium text-heading flex items-center">
        <span className="text-blue-600 dark:text-blue-400 mr-2">{icon}</span>
        {title}
      </h3>
      {action}
    </div>
  );
}

function EvaluationCard({
  evaluation,
  onEdit,
  onDelete,
}: {
  evaluation: MemberEvaluation;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[var(--border-default)] rounded-lg p-4">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left min-w-0">
          {open ? <ChevronDown className="h-4 w-4 text-meta flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-meta flex-shrink-0" />}
          <span className="text-sm text-heading">{formatDate(evaluation.evaluation_date)}</span>
          {evaluation.evaluator_name && <span className="text-xs text-meta truncate">by {evaluation.evaluator_name}</span>}
        </button>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button type="button" onClick={onEdit} className={linkBtn}><Pencil className="h-3.5 w-3.5" />Edit</button>
          <button type="button" onClick={onDelete} className={dangerLinkBtn}><Trash2 className="h-3.5 w-3.5" />Delete</button>
        </div>
      </div>
      {open && (
        <dl className="mt-3 space-y-3 pl-6">
          {([
            ['Strengths', evaluation.strengths],
            ['Weaknesses', evaluation.weaknesses],
            ['Goals', evaluation.goals],
            ['Employee feedback', evaluation.employee_feedback],
          ] as const).map(([label, val]) => (
            <div key={label}>
              <dt className="text-xs text-meta">{label}</dt>
              <dd className="mt-0.5 text-sm text-heading whitespace-pre-wrap">
                {val ? val : <span className="text-meta italic">—</span>}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function MemberHrPanel({ memberId }: { memberId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDesktop = useIsDesktop();

  const [activeTab, setActiveTab] = useState<HrTab>('personal');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<HrSection | null>(null);
  const [childModal, setChildModal] = useState<{ open: boolean; child: MemberChild | null }>({ open: false, child: null });
  const [evalModal, setEvalModal] = useState<{ open: boolean; evaluation: MemberEvaluation | null }>({ open: false, evaluation: null });
  const [deleteFile, setDeleteFile] = useState<MemberFile | null>(null);
  const [deleteChild, setDeleteChild] = useState<MemberChild | null>(null);
  const [deleteEval, setDeleteEval] = useState<MemberEvaluation | null>(null);

  const enabled = memberId > 0;
  const hrQuery = useQuery({ queryKey: ['member-hr', memberId], queryFn: () => hrService.getMemberHr(memberId), enabled });
  const childrenQuery = useQuery({ queryKey: ['member-children', memberId], queryFn: () => hrService.listChildren(memberId), enabled });
  const filesQuery = useQuery({ queryKey: ['member-files', memberId], queryFn: () => hrService.listFiles(memberId), enabled });
  const evalQuery = useQuery({ queryKey: ['member-evaluations', memberId], queryFn: () => hrService.listEvaluations(memberId), enabled });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => hrService.uploadFile(memberId, file),
    onSuccess: () => {
      toast('File uploaded', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-files', memberId] });
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: number) => hrService.deleteFile(memberId, fileId),
    onSuccess: () => {
      toast('File deleted', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-files', memberId] });
      setDeleteFile(null);
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const deleteChildMutation = useMutation({
    mutationFn: (childId: number) => hrService.deleteChild(memberId, childId),
    onSuccess: () => {
      toast('Child removed', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-children', memberId] });
      setDeleteChild(null);
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const deleteEvalMutation = useMutation({
    mutationFn: (evalId: number) => hrService.deleteEvaluation(memberId, evalId),
    onSuccess: () => {
      toast('Evaluation deleted', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-evaluations', memberId] });
      setDeleteEval(null);
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const handleDownload = async (file: MemberFile) => {
    setDownloadingId(file.id);
    try {
      await hrService.downloadFile(memberId, file);
    } catch {
      toast('Failed to download file', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (file) uploadMutation.mutate(file);
  };

  const hr = hrQuery.data;
  const children = childrenQuery.data ?? [];
  const files = filesQuery.data ?? [];
  const evaluations = evalQuery.data ?? [];

  const editAction = (section: HrSection) => (
    <button type="button" onClick={() => setEditingSection(section)} className={linkBtn}>
      <Pencil className="h-3.5 w-3.5" />Edit
    </button>
  );

  const HR_TABS: { key: HrTab; label: string; icon: ReactNode }[] = [
    { key: 'personal', label: 'Personal', icon: <User className="h-4 w-4" /> },
    { key: 'employment', label: 'Employment', icon: <Briefcase className="h-4 w-4" /> },
    { key: 'files', label: 'Files', icon: <FileText className="h-4 w-4" /> },
    { key: 'evaluations', label: 'Evaluations', icon: <ClipboardList className="h-4 w-4" /> },
  ];

  const renderLoaded = () => {
    if (!hr) return null;

    // Each section is defined once as a self-contained <section> (header + body,
    // no outer spacing) so the same element serves both the stacked mobile layout
    // and the desktop tab panels — spacing/dividers are applied by each layout.
    const personalSection = (
      <section>
        <SectionHeader icon={<User className="h-4 w-4" />} title="Personal Info" action={editAction('personal')} />
        <dl className="space-y-4">
          <Field label="National ID" value={hr.national_id} />
          <Field label="Address" value={hr.address} />
          <Field label="Personal Email" value={hr.personal_email} />
          <Field label="Phone" value={hr.phone} />
          <Field label="Emergency Contact" value={hr.emergency_contact} />
          <Field label="Patron Saint Day" value={hr.patron_saint} />
          <Field label="Religious Holiday" value={hr.religious_holiday} />
        </dl>
      </section>
    );

    const educationSection = (
      <section>
        <SectionHeader icon={<GraduationCap className="h-4 w-4" />} title="Education" action={editAction('education')} />
        <dl className="space-y-4">
          <Field label="Diploma" value={hr.diploma} />
          <Field label="Education Level" value={hr.education_level} />
          <Field label="Academic Title" value={hr.education_profession} />
        </dl>
      </section>
    );

    const employmentSection = (
      <section>
        <SectionHeader icon={<Briefcase className="h-4 w-4" />} title="Employment" action={editAction('employment')} />
        <dl className="space-y-4">
          <Field label="Contract Number" value={hr.contract_number} />
          <Field label="Contract Position" value={hr.job_position_official} />
          <Field label="Team Changes" value={hr.team_changes} />
          <Field label="Salary" value={hr.salary} />
          <Field label="Salary Changes" value={hr.salary_changes} />
        </dl>
      </section>
    );

    const vacationSection = (
      <section>
        <SectionHeader icon={<TreePalm className="h-4 w-4" />} title="Vacation" action={editAction('employment')} />
        <dl className="space-y-4">
          <Field label="Vacation Days" value={hr.vacation_days} />
        </dl>
      </section>
    );

    const filesSection = (
      <section>
        <SectionHeader
          icon={<FileText className="h-4 w-4" />}
          title="Files"
          action={
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className={`${linkBtn} disabled:opacity-50`}
            >
              <Upload className="h-3.5 w-3.5" />
              {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
            </button>
          }
        />
        {files.length === 0 ? (
          <p className="text-sm text-meta italic">No files uploaded</p>
        ) : (
          <ul className="space-y-3">
            {files.map((file) => {
              const { Icon, color } = fileIconFor(file.file_name);
              return (
              <li key={file.id} className="flex items-center justify-between gap-2 border border-[var(--border-default)] rounded-lg p-4">
                <div className="min-w-0 flex items-center gap-3">
                  <Icon className={`h-5 w-5 flex-shrink-0 ${color}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-heading truncate">{file.file_name}</p>
                    <p className="text-xs text-meta">{formatBytes(file.file_size)} · {formatDate(file.created_at)}</p>
                  </div>
                </div>
                <div className="ml-3 flex items-center gap-3 flex-shrink-0">
                  <button type="button" onClick={() => handleDownload(file)} disabled={downloadingId === file.id} className={`${linkBtn} disabled:opacity-50`}>
                    <Download className="h-3.5 w-3.5" />
                    {downloadingId === file.id ? 'Downloading…' : 'Download'}
                  </button>
                  <button type="button" onClick={() => setDeleteFile(file)} className={dangerLinkBtn}>
                    <Trash2 className="h-3.5 w-3.5" />Delete
                  </button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
        <p className="mt-2 text-xs text-meta">JPG, PNG, PDF, DOCX, XLSX · max 10 MB · 50 MB total</p>
      </section>
    );

    // `listClass` controls the child layout: one-per-row in the desktop Personal
    // tab (a narrow column), the responsive two-up grid in the mobile stack.
    const buildChildrenSection = (listClass: string) => (
      <section>
        <SectionHeader
          icon={<Users className="h-4 w-4" />}
          title="Children"
          action={
            <button type="button" onClick={() => setChildModal({ open: true, child: null })} className={linkBtn}>
              <Plus className="h-3.5 w-3.5" />Add
            </button>
          }
        />
        {children.length === 0 ? (
          <p className="text-sm text-meta italic">No children added</p>
        ) : (
          <ul className={listClass}>
            {children.map((child) => (
              <li key={child.id} className="text-sm text-heading flex items-center justify-between border border-[var(--border-default)] rounded-md px-3 py-2">
                <span className="min-w-0 truncate">{child.name}</span>
                <span className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-meta">{child.date_of_birth ? formatDate(child.date_of_birth) : '—'}</span>
                  <button type="button" onClick={() => setChildModal({ open: true, child })} className={linkBtn}><Pencil className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => setDeleteChild(child)} className={dangerLinkBtn}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    );

    const evaluationsSection = (
      <section>
        <SectionHeader
          icon={<ClipboardList className="h-4 w-4" />}
          title="Evaluations"
          action={
            <button type="button" onClick={() => setEvalModal({ open: true, evaluation: null })} className={linkBtn}>
              <Plus className="h-3.5 w-3.5" />Add Evaluation
            </button>
          }
        />
        {evaluations.length === 0 ? (
          <p className="text-sm text-meta italic">No evaluations recorded</p>
        ) : (
          <div className="space-y-3">
            {evaluations.map((evaluation) => (
              <EvaluationCard
                key={evaluation.id}
                evaluation={evaluation}
                onEdit={() => setEvalModal({ open: true, evaluation })}
                onDelete={() => setDeleteEval(evaluation)}
              />
            ))}
          </div>
        )}
      </section>
    );

    return (
      <>
        {hr.decrypt_error && (
          <div className="mb-4 rounded-md border border-amber-400 bg-surface-app px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
            Some encrypted values could not be decrypted right now and are shown as “Not set”.
          </div>
        )}

        {/* Single hidden file input, shared by whichever layout is mounted. */}
        <input ref={fileInputRef} type="file" accept={ACCEPTED_FILE_TYPES} className="hidden" onChange={handleFileSelected} />

        {isDesktop ? (
          /* ── Desktop (lg+): tabbed layout ─────────────────────────────── */
          <div>
            <div role="tablist" aria-label="HR record sections" className="grid grid-cols-4 border-b border-[var(--border-default)]">
              {HR_TABS.map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-t-md px-8 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      active
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-meta hover:text-blue-700 hover:bg-blue-50 dark:hover:text-blue-300 dark:hover:bg-blue-900/20'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* All four panels share one grid cell (grid-area 1/1), so the
                container is always as tall as the tallest panel — switching tabs
                never changes its height and the page doesn't jump. Inactive
                panels are visibility:hidden: kept in layout for the height calc,
                removed from the a11y tree and tab order. If a tab is ever taller
                than Personal, every tab lifts to that height. */}
            <div className="grid">
              <div
                role="tabpanel"
                aria-hidden={activeTab !== 'personal'}
                style={{ gridArea: '1 / 1' }}
                className={`pt-6 grid grid-cols-2 divide-x divide-[var(--border-default)] ${activeTab === 'personal' ? '' : 'invisible'}`}
              >
                <div className="pr-8">{personalSection}</div>
                <div className="pl-8 space-y-8">
                  {buildChildrenSection('space-y-2')}
                  {educationSection}
                </div>
              </div>

              <div
                role="tabpanel"
                aria-hidden={activeTab !== 'employment'}
                style={{ gridArea: '1 / 1' }}
                className={`pt-6 grid grid-cols-2 divide-x divide-[var(--border-default)] ${activeTab === 'employment' ? '' : 'invisible'}`}
              >
                <div className="pr-8">{employmentSection}</div>
                <div className="pl-8">{vacationSection}</div>
              </div>

              <div
                role="tabpanel"
                aria-hidden={activeTab !== 'files'}
                style={{ gridArea: '1 / 1' }}
                className={`pt-6 ${activeTab === 'files' ? '' : 'invisible'}`}
              >
                {filesSection}
              </div>

              <div
                role="tabpanel"
                aria-hidden={activeTab !== 'evaluations'}
                style={{ gridArea: '1 / 1' }}
                className={`pt-6 ${activeTab === 'evaluations' ? '' : 'invisible'}`}
              >
                {evaluationsSection}
              </div>
            </div>
          </div>
        ) : (
          /* ── Mobile / tablet (< lg): unchanged top-to-bottom stack ─────── */
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {personalSection}
              {educationSection}
            </div>
            <div className="mt-6 border-t border-[var(--border-default)] pt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {employmentSection}
              {vacationSection}
            </div>
            <div className="mt-6 border-t border-[var(--border-default)] pt-6">{filesSection}</div>
            <div className="mt-6 border-t border-[var(--border-default)] pt-6">
              {buildChildrenSection('grid grid-cols-1 sm:grid-cols-2 gap-2')}
            </div>
            <div className="mt-6 border-t border-[var(--border-default)] pt-6">{evaluationsSection}</div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="bg-surface shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-4 sm:px-6">
        <h2 className="text-lg font-semibold text-heading flex items-center">
          <Lock className="h-5 w-5 text-meta mr-2" />
          HR Records
        </h2>
      </div>

      <div className="px-4 pb-5 sm:px-6">
        {hrQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" text="Loading HR records..." />
          </div>
        ) : hrQuery.error || !hr ? (
          <p className="text-sm text-red-600 dark:text-red-400">Could not load HR records.</p>
        ) : (
          renderLoaded()
        )}
      </div>

      {/* Modals */}
      {hr && editingSection && (
        <HrSectionModal
          isOpen={!!editingSection}
          onClose={() => setEditingSection(null)}
          memberId={memberId}
          section={editingSection}
          hr={hr}
        />
      )}
      <HrChildModal
        isOpen={childModal.open}
        onClose={() => setChildModal({ open: false, child: null })}
        memberId={memberId}
        child={childModal.child}
      />
      <HrEvaluationModal
        isOpen={evalModal.open}
        onClose={() => setEvalModal({ open: false, evaluation: null })}
        memberId={memberId}
        evaluation={evalModal.evaluation}
      />
      <DeleteConfirmModal
        isOpen={!!deleteFile}
        onClose={() => setDeleteFile(null)}
        onConfirm={() => deleteFile && deleteFileMutation.mutate(deleteFile.id)}
        title="Delete File"
        message="This file will be permanently removed."
        itemName={deleteFile?.file_name}
        isDeleting={deleteFileMutation.isPending}
      />
      <DeleteConfirmModal
        isOpen={!!deleteChild}
        onClose={() => setDeleteChild(null)}
        onConfirm={() => deleteChild && deleteChildMutation.mutate(deleteChild.id)}
        title="Remove Child"
        message="This child record will be permanently removed."
        itemName={deleteChild?.name}
        isDeleting={deleteChildMutation.isPending}
      />
      <DeleteConfirmModal
        isOpen={!!deleteEval}
        onClose={() => setDeleteEval(null)}
        onConfirm={() => deleteEval && deleteEvalMutation.mutate(deleteEval.id)}
        title="Delete Evaluation"
        message="This evaluation will be permanently removed."
        itemName={deleteEval ? formatDate(deleteEval.evaluation_date) : undefined}
        isDeleting={deleteEvalMutation.isPending}
      />
    </div>
  );
}
